import { useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';

interface ParallaxProps {
  children: ReactNode;
  /**
   * Pixels of travel across the element's full pass through the viewport.
   * Negative moves against the scroll (drifts up / left). Default -60.
   */
  speed?: number;
  axis?: 'x' | 'y';
  className?: string;
  style?: CSSProperties;
}

/**
 * Scroll-linked drift. Maps the element's progress through the viewport to a
 * translate on one axis. Renders a plain `<div>` (no transform) under reduced
 * motion.
 *
 *   <Parallax speed={-80}><Blob … /></Parallax>
 */
export function Parallax({ children, speed = -60, axis = 'y', className, style }: ParallaxProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const move = useTransform(scrollYProgress, [0, 1], [-speed, speed]);

  if (reduced) {
    return (
      <div ref={ref} className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={axis === 'x' ? { ...style, x: move } : { ...style, y: move }}
    >
      {children}
    </motion.div>
  );
}
